import { useMemo } from "react";
import { progressPct } from "@/lib/progress";
import { phaseColor } from "@/lib/constants";
import {
  CheckCircle2, Flag, PencilRuler, Hammer, Rocket, Target, Layers, Package, ClipboardCheck,
} from "lucide-react";

const ICONS = [Flag, PencilRuler, Hammer, Rocket, Target, Layers, Package, ClipboardCheck];
const isDone = (t) => t.health === "blue" || t.status === "done" || (t.percent_complete || 0) >= 100;
const effPct = (t) => (t.percent_complete != null ? Math.max(0, Math.min(100, t.percent_complete)) : (isDone(t) ? 100 : 0));

export default function PhaseProgress({ tasks = [], phases, phaseColors, progressStyle }) {
  const list = useMemo(() => (phases && phases.length ? phases : [...new Set(tasks.map((t) => t.phase).filter(Boolean))]), [phases, tasks]);
  const colorOf = (p, i) => (phaseColors && phaseColors[p]) || phaseColor(p) || ["#0044FF", "#EF4444", "#10B981", "#F59E0B", "#7C3AED"][i % 5];

  const stats = useMemo(() => list.map((ph) => {
    const items = tasks.filter((t) => (t.phase || "") === ph);
    const total = items.length;
    const done = items.filter(isDone).length;
    const pct = total ? progressPct(items, progressStyle) : 0;
    const today = new Date().toISOString().slice(0, 10);
    const trouble = items.some((t) => !isDone(t) && (t.track_status === "jeopardy" || (t.end_date && t.end_date < today)));
    return { phase: ph, total, done, pct, complete: total > 0 && done === total, started: pct > 0 || done > 0, trouble };
  }), [tasks, list, progressStyle]);

  if (!list.length) return null;
  const completeCount = stats.filter((s) => s.complete).length;
  const headline = completeCount === list.length
    ? `All ${list.length} phases complete.`
    : `${completeCount} of ${list.length} phases complete.`;

  return (
    <div className="border border-border rounded-lg p-6 mb-6" data-testid="phase-progress">
      <p className="overline text-muted-foreground">Your progress</p>
      <h3 className="font-display text-lg font-bold mt-1">{headline}</h3>

      <div className="flex items-start gap-0 overflow-x-auto pb-2 pt-3 px-1 mt-3">
        {stats.map((s, i) => {
          const Icon = ICONS[i % ICONS.length];
          const c = colorOf(s.phase, i);
          const op = s.complete ? 1 : s.started ? 0.72 : 0.32;
          return (
            <div key={s.phase} className="flex items-start shrink-0">
              <div className="flex flex-col items-center gap-2 w-[92px]" data-testid={`phase-node-${i}`}>
                <div className="relative">
                  <div
                    className={`h-14 w-14 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-105 ${s.trouble && !s.complete ? "phase-bubble-trouble" : ""}`}
                    style={{ background: c, opacity: op, boxShadow: `0 6px 16px -4px ${c}80` }}
                    title={s.trouble && !s.complete ? `${s.done}/${s.total} tasks complete — this phase has overdue or jeopardy work` : `${s.done}/${s.total} tasks complete`}
                    data-testid={s.trouble && !s.complete ? `phase-bubble-trouble-${i}` : undefined}
                  >
                    <Icon className="h-6 w-6 text-white" strokeWidth={2.4} />
                  </div>
                  {s.complete && (
                    <span className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-white flex items-center justify-center shadow" data-testid={`phase-check-${i}`}>
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    </span>
                  )}
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wide text-center leading-tight" style={{ color: c }}>{s.phase}</span>
                <span className="text-[10px] text-muted-foreground -mt-1">{s.done}/{s.total} · {s.pct}%</span>
              </div>
              {i < stats.length - 1 && (
                <div className="h-14 flex items-center px-1">
                  <div className="h-1 w-10 sm:w-16 rounded-full" style={{ background: s.complete ? c : "#e2e8f0" }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
